import React from "react";
import { getTextColorForAccent, isDarkModeActive } from "./teamColors";

/**
 * JerseyNumber — shared component for displaying jersey numbers across the site.
 *
 * Two visual modes:
 *
 *  "inline"  — number rendered as bold italic text in the team color, on a neutral
 *              (white / dark-gray) background. Uses a layered text-shadow to create
 *              a visible outline that works on both light and dark page backgrounds.
 *              The shadow color is computed from the page background so the outline
 *              is always legible regardless of team color.
 *
 *  "badge"   — number rendered inside a small pill/chip whose background is the
 *              team accent color. Text color is auto-computed for contrast.
 *              Includes a subtle inner glow + drop-shadow for depth.
 *
 * Props:
 *   number      {number|string}  The jersey number (without #)
 *   color       {string}         Hex color for the number (team primary or accent)
 *   size        {"xs"|"sm"|"md"|"lg"|"xl"}  Controls font size (default "sm")
 *   mode        {"inline"|"badge"}           Display mode (default "inline")
 *   bgColor     {string}         Hex of the background the number sits on.
 *                                Used in "inline" mode to compute outline color.
 *                                Defaults to page background (#ffffff / #111827).
 *   className   {string}         Extra Tailwind classes on the wrapper
 */

const SIZE_MAP = {
  xs: {
    fontSize: "10px",
    lineHeight: "14px",
    minWidth: "18px",
    padding: "0 2px",
  },
  sm: {
    fontSize: "12px",
    lineHeight: "16px",
    minWidth: "22px",
    padding: "0 3px",
  },
  md: {
    fontSize: "15px",
    lineHeight: "20px",
    minWidth: "28px",
    padding: "0 4px",
  },
  lg: {
    fontSize: "20px",
    lineHeight: "26px",
    minWidth: "36px",
    padding: "0 5px",
  },
  xl: {
    fontSize: "28px",
    lineHeight: "34px",
    minWidth: "48px",
    padding: "0 6px",
  },
};

const BADGE_SIZE_MAP = {
  xs: {
    fontSize: "9px",
    lineHeight: "12px",
    padding: "1px 5px",
    borderRadius: "4px",
  },
  sm: {
    fontSize: "11px",
    lineHeight: "14px",
    padding: "2px 6px",
    borderRadius: "5px",
  },
  md: {
    fontSize: "13px",
    lineHeight: "17px",
    padding: "3px 8px",
    borderRadius: "6px",
  },
  lg: {
    fontSize: "17px",
    lineHeight: "22px",
    padding: "4px 10px",
    borderRadius: "7px",
  },
  xl: {
    fontSize: "24px",
    lineHeight: "30px",
    padding: "5px 14px",
    borderRadius: "9px",
  },
};

/**
 * Compute perceived brightness of a hex color (0–255).
 */
function hexBrightness(hex) {
  const c = hex.replace("#", "");
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return r * 0.2126 + g * 0.7152 + b * 0.0722;
}

/**
 * Ensure `color` has sufficient contrast (≥60 brightness delta) against
 * `bgColor`. If not, return a safe fallback (near-black or near-white).
 */
function ensureContrast(color, bgColor, isDark) {
  const contrast = Math.abs(hexBrightness(color) - hexBrightness(bgColor));
  if (contrast >= 60) return color;
  // Fall back to a readable neutral
  return isDark ? "#e5e7eb" : "#374151";
}

/**
 * Build a layered text-shadow that creates a visible outline effect.
 * The shadow color is the bg color, creating a clean knockout outline.
 *
 * @param {string} textColor   - The jersey number color (hex)
 * @param {string} bgColor     - The background color behind the number (hex)
 * @returns {string}           - CSS text-shadow value
 */
function buildOutlineShadow(textColor, bgColor) {
  const outline = bgColor;
  const spread = "0.6px";

  // 8-direction outline using the background color (creates clean edge)
  const outlineLayer = [
    `-${spread} -${spread} 0 ${outline}`,
    ` ${spread} -${spread} 0 ${outline}`,
    `-${spread}  ${spread} 0 ${outline}`,
    ` ${spread}  ${spread} 0 ${outline}`,
    `-${spread}  0         0 ${outline}`,
    ` ${spread}  0         0 ${outline}`,
    ` 0         -${spread} 0 ${outline}`,
    ` 0          ${spread} 0 ${outline}`,
  ].join(", ");

  // Subtle colored drop-shadow for depth
  const dropShadow = `2px 3px 6px ${textColor}55`;

  return `${outlineLayer}, ${dropShadow}`;
}

export default function JerseyNumber({
  number,
  color = "#6b7280",
  size = "sm",
  mode = "inline",
  bgColor,
  className = "",
}) {
  const isDark = isDarkModeActive();

  // Resolve background color for outline / contrast computation.
  // In the box score table the card bg is bg-white / dark:bg-gray-800.
  const resolvedBg = bgColor ?? (isDark ? "#1f2937" : "#ffffff");

  if (number == null || number === "") return null;

  // ── Badge mode ────────────────────────────────────────────────────────────
  if (mode === "badge") {
    const textColor = getTextColorForAccent(color, isDark);
    const sz = BADGE_SIZE_MAP[size] ?? BADGE_SIZE_MAP.sm;

    // Inner glow: use a lighter/darker tint of the badge color
    const isLightText = textColor === "#ffffff";
    const innerGlow = isLightText
      ? `inset 0 1px 0 rgba(255,255,255,0.25), 0 2px 6px ${color}88`
      : `inset 0 1px 0 rgba(255,255,255,0.40), 0 2px 6px ${color}55`;

    return (
      <span
        className={`inline-flex items-center justify-center font-black tabular-nums shrink-0 ${className}`}
        style={{
          background: color,
          color: textColor,
          fontSize: sz.fontSize,
          lineHeight: sz.lineHeight,
          padding: sz.padding,
          borderRadius: sz.borderRadius,
          fontStyle: "italic",
          letterSpacing: "-0.02em",
          boxShadow: innerGlow,
          fontFamily: "'Arial Narrow', 'Arial', sans-serif",
        }}
      >
        #{number}
      </span>
    );
  }

  // ── Inline mode ───────────────────────────────────────────────────────────
  // Guarantee the text color contrasts against the resolved background.
  // This prevents white-on-white (light mode) and dark-on-dark (dark mode).
  const safeColor = ensureContrast(color, resolvedBg, isDark);
  const sz = SIZE_MAP[size] ?? SIZE_MAP.sm;
  const shadow = buildOutlineShadow(safeColor, resolvedBg);

  return (
    <span
      className={`inline-flex items-center justify-center font-black tabular-nums shrink-0 ${className}`}
      style={{
        color: safeColor,
        fontSize: sz.fontSize,
        lineHeight: sz.lineHeight,
        minWidth: sz.minWidth,
        padding: sz.padding,
        fontStyle: "italic",
        letterSpacing: "-0.03em",
        textShadow: shadow,
        fontFamily: "'Arial Narrow', 'Arial', sans-serif",
      }}
    >
      #{number}
    </span>
  );
}
